import React, { ReactNode, useEffect, useState } from 'react'
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native'
import styled from 'styled-components/native'
import { useMutation } from '@apollo/client'
import { Feather } from '../../../components/icon/vectorIcons'
import Modal from '../../../components/common/Modal'
import useTranslation from '../../../hooks/useTranslation'
import {
  publishShareDocletMutation,
  ShareDocletData,
  unpublishShareDocletMutation,
  updateShareDocletGatingMutation
} from '../api'
import { builderColors, builderRadii } from './builderColors'
import { PANEL_BODY, PANEL_CONTENT, PANEL_OVERLAY } from './sidePanel'
import { SaveStatus } from './useDocletAutosave'
import {
  GATING_MODALITIES,
  GatingSettings,
  gatingChangeAffectsLink,
  gatingFromDoclet,
  gatingInput,
  addInvitees,
  gatingProblem,
  parseInvitees,
  publishActionFor,
  sameGating
} from './sharing'

interface PublishSharingPanelProps {
  doclet: ShareDocletData
  open: boolean
  onClose: () => void
  // From the builder's autosave: publishing must name the saved draft
  saveStatus: SaveStatus
  getDraftVersion: () => number
}

/**
 * Publish & sharing: the doclet's status (draft or published), its share
 * link, and who can open the link. Changes are staged and applied with Done;
 * Cancel or the close button drops them.
 *
 * Publishing serves the last saved draft, so it waits for autosave to catch
 * up. A published doclet with newer edits offers to publish them too.
 *
 * Done saves gating before publishing, so a new link never opens with the
 * old gating.
 */
const PublishSharingPanel = ({
  doclet,
  open,
  onClose,
  saveStatus,
  getDraftVersion
}: PublishSharingPanelProps) => {
  const { t } = useTranslation()
  const [publish] = useMutation(publishShareDocletMutation)
  const [unpublish] = useMutation(unpublishShareDocletMutation)
  const [updateGating] = useMutation(updateShareDocletGatingMutation)
  const gating = gatingFromDoclet(doclet.gating)
  const [wantPublished, setWantPublished] = useState(doclet.isPublished)
  const [publishLatest, setPublishLatest] = useState(true)
  const [draftGating, setDraftGating] = useState(gating)
  const [inviteText, setInviteText] = useState('')
  // Entries refused when last added, as typed
  const [invalidInvitees, setInvalidInvitees] = useState<string[]>([])
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  // Just published for the first time: stay open so the new link can be
  // copied straight away
  const [justPublished, setJustPublished] = useState(false)

  // Each opening starts from the doclet's current state
  useEffect(() => {
    if (!open) return
    setWantPublished(doclet.isPublished)
    setPublishLatest(true)
    setDraftGating(gating)
    setInviteText('')
    setInvalidInvitees([])
    setError(null)
    setCopied(false)
    setJustPublished(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const action = publishActionFor(doclet, wantPublished, publishLatest)
  // Done adds anything still typed in the invite box, as Enter would, so
  // gating is judged with it. Text it would refuse leaves Done available:
  // pressing it names the refused entries instead.
  const pendingInvitees =
    draftGating.modality === 'invited'
      ? parseInvitees(inviteText)
      : { valid: [], invalid: [] }
  const problem = pendingInvitees.invalid.length
    ? null
    : gatingProblem(addInvitees(draftGating, pendingInvitees.valid))
  const waitingForSave = action === 'publish' && saveStatus !== 'saved'
  const blocked = !!problem || waitingForSave || applying
  // The first publish creates the share link, so it says what it does and
  // keeps the panel open afterwards
  const firstPublish = action === 'publish' && !doclet.shareUrl

  const setModality = (modality: GatingSettings['modality']) =>
    setDraftGating(current => ({ ...current, modality }))

  /**
   * Adds what's typed in the invite box (one entry or a pasted list). Refused
   * entries stay in the box, named in the error, to be corrected. Returns the
   * gating with the valid ones added, and whether any were refused.
   */
  const addTypedInvitees = () => {
    const { valid, invalid } = parseInvitees(inviteText)
    const next = addInvitees(draftGating, valid)
    setDraftGating(next)
    setInviteText(invalid.join(', '))
    setInvalidInvitees(invalid)
    return { next, refused: invalid.length > 0 }
  }

  const removeInvitee = (invitee: string) =>
    setDraftGating(current => ({
      ...current,
      invitees: current.invitees.filter(entry => entry !== invitee)
    }))

  const copyLink = async () => {
    if (!doclet.shareUrl) return
    try {
      await navigator.clipboard.writeText(doclet.shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError(t('doclets:sharing:copyFailed'))
    }
  }

  const done = async () => {
    if (blocked) return
    let toApply = draftGating
    if (draftGating.modality === 'invited' && inviteText.trim()) {
      const { next, refused } = addTypedInvitees()
      if (refused) return
      toApply = next
    }
    setApplying(true)
    setError(null)
    if (!sameGating(gating, toApply)) {
      try {
        await updateGating({
          variables: { id: doclet.id, gating: gatingInput(toApply) }
        })
      } catch {
        setError(t('doclets:sharing:gatingSaveFailed'))
        setApplying(false)
        return
      }
    }
    try {
      if (action === 'publish') {
        await publish({
          variables: { id: doclet.id, baseDraftVersion: getDraftVersion() }
        })
      } else if (action === 'unpublish') {
        await unpublish({ variables: { id: doclet.id } })
      }
      if (firstPublish) setJustPublished(true)
      else onClose()
    } catch {
      setError(
        t(
          action === 'unpublish'
            ? 'doclets:sharing:unpublishFailed'
            : 'doclets:sharing:publishFailed'
        )
      )
    } finally {
      setApplying(false)
    }
  }

  const showLatestEdits =
    wantPublished && doclet.isPublished && doclet.hasUnpublishedChanges

  return (
    <Modal
      open={open}
      close={onClose}
      overlayStyle={PANEL_OVERLAY}
      contentStyle={PANEL_CONTENT}
      bodyStyle={{ ...PANEL_BODY, padding: 0 }}
    >
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 24 }}>
        <Header>
          <Title>{t('doclets:sharing:title')}</Title>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('doclets:sharing:close')}
            onPress={onClose}
          >
            <Feather name="x" size={20} color={builderColors.text} />
          </Pressable>
        </Header>
        <Intro>{t('doclets:sharing:intro')}</Intro>

        <SectionLabel>{t('doclets:sharing:status')}</SectionLabel>
        <OptionCard
          selected={!wantPublished}
          onPress={() => setWantPublished(false)}
          label={t('doclets:sharing:draft')}
          description={t('doclets:sharing:draftHelp')}
        />
        <OptionCard
          selected={wantPublished}
          onPress={() => setWantPublished(true)}
          label={t('doclets:sharing:published')}
          description={t('doclets:sharing:publishedHelp')}
        >
          {showLatestEdits && (
            <Checkbox
              checked={publishLatest}
              onToggle={() => setPublishLatest(value => !value)}
              label={t('doclets:sharing:publishLatest')}
              help={t('doclets:sharing:publishLatestHelp')}
            />
          )}
        </OptionCard>
        {waitingForSave && <Note>{t('doclets:sharing:waitingForSave')}</Note>}

        <Divider />
        <SectionLabel>{t('doclets:sharing:shareLink')}</SectionLabel>
        {justPublished && (
          <Success>{t('doclets:sharing:justPublished')}</Success>
        )}
        {doclet.shareUrl ? (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <LinkBox>
                <Text
                  numberOfLines={1}
                  selectable
                  style={{ fontSize: 13, color: builderColors.text }}
                >
                  {doclet.shareUrl}
                </Text>
              </LinkBox>
              <PanelButton
                label={
                  copied
                    ? t('doclets:sharing:copied')
                    : t('doclets:sharing:copyLink')
                }
                onPress={copyLink}
                style={{ marginLeft: 8 }}
              />
            </View>
            {!wantPublished && (
              <Note>{t('doclets:sharing:linkWhileDraft')}</Note>
            )}
          </>
        ) : (
          <Note>{t('doclets:sharing:noLinkYet')}</Note>
        )}

        <Divider />
        <SectionLabel>{t('doclets:sharing:gating')}</SectionLabel>
        {GATING_MODALITIES.map(({ modality, friction }) => {
          const selected = draftGating.modality === modality
          return (
            <OptionCard
              key={modality}
              selected={selected}
              onPress={() => setModality(modality)}
              label={t(`doclets:sharing:modalities:${modality}`)}
              tag={t(`doclets:sharing:friction:${friction}`)}
              description={t(`doclets:sharing:modalities:${modality}Help`)}
            >
              {selected && modality === 'password' && (
                <PanelInput
                  value={draftGating.password}
                  onChangeText={password =>
                    setDraftGating(current => ({ ...current, password }))
                  }
                  placeholder={t('doclets:sharing:passwordPlaceholder')}
                  accessibilityLabel={t('doclets:sharing:password')}
                  autoCapitalize="none"
                  style={{ maxWidth: 240 }}
                />
              )}
              {selected && modality === 'invited' && (
                <View style={{ marginTop: 8 }}>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {draftGating.invitees.map(invitee => (
                      <Chip key={invitee}>
                        <ChipText>{invitee}</ChipText>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t('doclets:sharing:remove', {
                            invitee
                          })}
                          onPress={() => removeInvitee(invitee)}
                          style={{ marginLeft: 6 }}
                        >
                          <Feather
                            name="x-circle"
                            size={16}
                            color={builderColors.text}
                          />
                        </Pressable>
                      </Chip>
                    ))}
                  </View>
                  <PanelInput
                    value={inviteText}
                    onChangeText={text => {
                      setInviteText(text)
                      setInvalidInvitees([])
                    }}
                    onSubmitEditing={addTypedInvitees}
                    blurOnSubmit={false}
                    placeholder={t('doclets:sharing:invitePlaceholder')}
                    accessibilityLabel={t('doclets:sharing:invitePlaceholder')}
                    autoCapitalize="none"
                  />
                  {invalidInvitees.length > 0 && (
                    <ErrorText>
                      {t('doclets:sharing:inviteInvalid', {
                        entries: invalidInvitees.join(', ')
                      })}
                    </ErrorText>
                  )}
                </View>
              )}
            </OptionCard>
          )
        })}
        {!!problem && (
          <ErrorText>{t(`doclets:sharing:problems:${problem}`)}</ErrorText>
        )}
        {gatingChangeAffectsLink(doclet.isPublished, gating, draftGating) && (
          <Warning>
            <Feather
              name="alert-triangle"
              size={14}
              color={builderColors.text}
            />
            <WarningText>{t('doclets:sharing:changeAffectsLink')}</WarningText>
          </Warning>
        )}
        {!!error && <ErrorText>{error}</ErrorText>}
      </ScrollView>

      <Footer>
        <FooterNote>{t('doclets:sharing:confidentiality')}</FooterNote>
        {/* Nothing to cancel once the first publish has gone out */}
        {!justPublished && (
          <PanelButton label={t('doclets:sharing:cancel')} onPress={onClose} />
        )}
        <PanelButton
          primary
          label={
            applying
              ? t('doclets:sharing:applying')
              : firstPublish
              ? t('doclets:sharing:publish')
              : t('doclets:sharing:done')
          }
          onPress={done}
          disabled={blocked}
          style={{ marginLeft: 8 }}
        />
      </Footer>
    </Modal>
  )
}

interface OptionCardProps {
  selected: boolean
  onPress: () => void
  label: string
  description: string
  tag?: string
  children?: ReactNode
}

// A radio option as a card: selected cards are tinted, and can hold that
// option's own fields
const OptionCard = ({
  selected,
  onPress,
  label,
  description,
  tag,
  children
}: OptionCardProps) => {
  const [hovered, setHovered] = useState(false)
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 14,
        marginBottom: 8,
        borderWidth: 1,
        borderRadius: builderRadii.md,
        borderColor: selected ? builderColors.hoverBorder : 'transparent',
        backgroundColor: selected
          ? builderColors.selectedFill
          : hovered
          ? '#F7F7F7'
          : 'transparent'
      }}
    >
      <Radio selected={selected} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <OptionLabel>{label}</OptionLabel>
          {!!tag && (
            <Tag>
              <TagText>{tag}</TagText>
            </Tag>
          )}
        </View>
        <OptionHelp>{description}</OptionHelp>
        {children}
      </View>
    </Pressable>
  )
}

const Radio = ({ selected }: { selected: boolean }) => (
  <View
    style={{
      width: 18,
      height: 18,
      marginTop: 2,
      marginRight: 14,
      borderRadius: 9,
      borderWidth: 1.5,
      borderColor: selected ? builderColors.text : builderColors.mutedText,
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {selected && (
      <View
        style={{
          width: 10,
          height: 10,
          borderRadius: 5,
          backgroundColor: builderColors.text
        }}
      />
    )}
  </View>
)

interface CheckboxProps {
  checked: boolean
  onToggle: () => void
  label: string
  help: string
}

const Checkbox = ({ checked, onToggle, label, help }: CheckboxProps) => (
  <Pressable
    accessibilityRole="checkbox"
    accessibilityState={{ checked }}
    onPress={onToggle}
    style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 10 }}
  >
    <View
      style={{
        width: 16,
        height: 16,
        marginTop: 1,
        marginRight: 8,
        borderRadius: 3,
        borderWidth: 1.5,
        borderColor: builderColors.text,
        backgroundColor: checked ? builderColors.text : 'transparent',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {checked && <Feather name="check" size={12} color="#FFFFFF" />}
    </View>
    <View style={{ flex: 1 }}>
      <Text
        style={{ fontSize: 13, fontWeight: '600', color: builderColors.text }}
      >
        {label}
      </Text>
      <OptionHelp>{help}</OptionHelp>
    </View>
  </Pressable>
)

interface PanelButtonProps {
  label: string
  onPress: () => void
  primary?: boolean
  disabled?: boolean
  style?: object
}

const PanelButton = ({
  label,
  onPress,
  primary = false,
  disabled = false,
  style
}: PanelButtonProps) => (
  <Pressable
    accessibilityRole="button"
    accessibilityState={{ disabled }}
    disabled={disabled}
    onPress={onPress}
    style={[
      {
        height: 40,
        paddingHorizontal: 18,
        justifyContent: 'center',
        borderWidth: 1,
        borderRadius: builderRadii.md,
        borderColor: primary ? builderColors.text : builderColors.border,
        backgroundColor: primary ? builderColors.text : '#FFFFFF',
        opacity: disabled ? 0.4 : 1
      },
      style
    ]}
  >
    <Text
      style={{
        fontSize: 14,
        fontWeight: '600',
        color: primary ? '#FFFFFF' : builderColors.text
      }}
    >
      {label}
    </Text>
  </Pressable>
)

const PanelInput = (props: React.ComponentProps<typeof TextInput>) => (
  <TextInput
    {...props}
    style={[
      {
        height: 40,
        marginTop: 8,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderColor: builderColors.border,
        borderRadius: builderRadii.md,
        backgroundColor: '#FFFFFF',
        fontSize: 14,
        color: builderColors.text
      },
      props.style
    ]}
  />
)

const Header = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const Title = styled(Text)`
  font-size: 22px;
  font-weight: 700;
  color: ${builderColors.text};
`

const Intro = styled(Text)`
  font-size: 13px;
  line-height: 19px;
  color: ${builderColors.mutedText};
  margin-bottom: 24px;
`

const SectionLabel = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${builderColors.text};
  margin-bottom: 12px;
`

const OptionLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${builderColors.text};
`

const OptionHelp = styled(Text)`
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: ${builderColors.mutedText};
`

const Tag = styled(View)`
  margin-left: 8px;
  padding: 1px 8px;
  border-width: 1px;
  border-color: ${builderColors.border};
  border-radius: 10px;
`

const TagText = styled(Text)`
  font-size: 11px;
  color: ${builderColors.mutedText};
`

const Divider = styled(View)`
  height: 1px;
  margin: 20px 0;
  background-color: ${builderColors.border};
`

const LinkBox = styled(View)`
  flex: 1;
  min-width: 0;
  height: 40px;
  justify-content: center;
  padding: 0 12px;
  border-width: 1px;
  border-color: ${builderColors.border};
  border-radius: ${builderRadii.md}px;
  background-color: #f7f7f7;
`

const Note = styled(Text)`
  margin-top: 8px;
  font-size: 12px;
  line-height: 17px;
  color: ${builderColors.mutedText};
`

const Chip = styled(View)`
  flex-direction: row;
  align-items: center;
  margin: 0 8px 8px 0;
  padding: 4px 8px 4px 12px;
  border-width: 1px;
  border-color: ${builderColors.border};
  border-radius: 16px;
  background-color: #ffffff;
`

const ChipText = styled(Text)`
  font-size: 13px;
  color: ${builderColors.text};
`

const Warning = styled(View)`
  flex-direction: row;
  align-items: flex-start;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: ${builderRadii.md}px;
  background-color: #fff6e0;
`

const WarningText = styled(Text)`
  flex: 1;
  margin-left: 8px;
  font-size: 12px;
  line-height: 17px;
  color: ${builderColors.text};
`

const Success = styled(Text)`
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${builderColors.text};
`

const ErrorText = styled(Text)`
  margin-top: 8px;
  font-size: 12px;
  color: ${builderColors.danger};
`

const Footer = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: 16px 24px;
  border-top-width: 1px;
  border-top-color: ${builderColors.border};
  background-color: #ffffff;
`

const FooterNote = styled(Text)`
  flex: 1;
  margin-right: 16px;
  font-size: 12px;
  line-height: 17px;
  font-style: italic;
  color: ${builderColors.mutedText};
`

export default PublishSharingPanel
